import { useState, useEffect, useCallback } from "react";
import { Loader2, Swords, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface RehearsalSession {
  id: string;
  created_at: string;
  readiness_score: number | null;
  tactics_sequence: any;
  messages: any;
  status: string;
}

const RehearsalHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<RehearsalSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("rehearsal_sessions")
      .select("id, created_at, readiness_score, tactics_sequence, messages, status")
      .eq("user_id", user.id)
      .eq("status", "completed")
      .order("created_at", { ascending: false })
      .limit(20);

    if (!error) setSessions((data as RehearsalSession[]) || []);
    setIsLoading(false);
  }, [user]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const getPrimaryTactic = (session: RehearsalSession): string => {
    // Try tactics_sequence first
    const seq = session.tactics_sequence;
    if (Array.isArray(seq) && seq.length > 0) {
      const first = typeof seq[0] === "string" ? seq[0] : seq[0]?.tactic || seq[0]?.tag;
      if (first) return String(first).replace(/_/g, " ");
    }
    // Fallback: first tactic_tag in messages
    const msgs = session.messages;
    if (Array.isArray(msgs)) {
      for (const m of msgs) {
        if (m?.tactic_tag) return String(m.tactic_tag).replace(/_/g, " ");
      }
    }
    return "General";
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div>
          <h2 className="text-xl font-bold mb-1">
            <span className="text-foreground">Rehearsal </span>
            <span className="text-gradient-cyan">History</span>
          </h2>
          <p className="text-muted-foreground text-sm">Your past simulation sessions</p>
        </div>
        <div className="flex items-center justify-center h-24">
          <Loader2 className="w-6 h-6 text-cyan animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-xl font-bold mb-1">
          <span className="text-foreground">Rehearsal </span>
          <span className="text-gradient-cyan">History</span>
        </h2>
        <p className="text-muted-foreground text-sm">Your past simulation sessions</p>
      </div>

      {sessions.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <Swords className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-muted-foreground text-sm">
            No rehearsal sessions yet. Start a rehearsal from any analysis result.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {/* Header */}
          <div className="hidden md:grid grid-cols-[90px_1fr_70px_auto] gap-3 px-4 py-2.5 border-b border-border bg-secondary/30">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Date</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Primary Tactic</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Score</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono text-right">Actions</span>
          </div>

          <div className="divide-y divide-border">
            {sessions.map((s) => {
              const date = new Date(s.created_at);
              const tactic = getPrimaryTactic(s);
              const score = s.readiness_score != null ? `${s.readiness_score}/5` : "—";

              return (
                <div key={s.id} className="w-full px-4 py-3 hover:bg-secondary/40 transition-colors">
                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-[90px_1fr_70px_auto] gap-3 items-center">
                    <span className="text-xs text-muted-foreground font-mono">
                      {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                    </span>
                    <span className="text-sm text-foreground/80 capitalize">{tactic}</span>
                    <span className={cn("text-sm font-mono font-bold", s.readiness_score != null && s.readiness_score >= 4 ? "text-cyan" : s.readiness_score != null && s.readiness_score >= 2 ? "text-amber-400" : "text-muted-foreground")}>
                      {score}
                    </span>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/rehearsal/debrief/${s.id}`)}
                        className="h-7 px-2 text-[11px] text-cyan hover:text-cyan hover:bg-cyan/10"
                      >
                        View Debrief <ArrowRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="md:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-mono">
                        {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                      <span className={cn("text-xs font-mono font-bold", s.readiness_score != null && s.readiness_score >= 4 ? "text-cyan" : "text-muted-foreground")}>
                        {score}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground/80 capitalize">{tactic}</span>
                      <button
                        onClick={() => navigate(`/rehearsal/debrief/${s.id}`)}
                        className="text-xs text-cyan font-semibold"
                      >
                        Debrief →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-4 py-2 border-t border-border bg-secondary/20">
            <span className="text-[10px] text-muted-foreground font-mono">
              {sessions.length} completed {sessions.length === 1 ? "session" : "sessions"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RehearsalHistory;
